<template>
    <div>
        <DataTable
            :data="sortedOrgs || []"
            :columns="progressColumns"
            :loading="isLoading"
            enable-search
            enable-pagination
            enable-sorting
            @row-click="handleRowClick"
        />
    </div>
</template>

<script setup lang="ts">
import { h } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import DataTable from '@/components/tables/DataTable.vue';
import { createStatusWithDotColumn, createFullNameColumn } from '@/components/tables/columns/factories';
import type { AdminOrganizationFormData, SortableAdminOrganizationFormData } from '@/stores/store-interfaces';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { TagVariant } from '@/components/tables/types';

defineProps<{
    isLoading: boolean;
    sortedOrgs: AdminOrganizationFormData[] | undefined;
}>();

const emit = defineEmits<{
    sort: [fieldName: string];
    choose: [id: number];
}>();

function handleRowClick(org: AdminOrganizationFormData) {
    emit('choose', org.organization_creation_form_id);
}

// Column definitions
const progressColumns: ColumnDef<AdminOrganizationFormData, unknown>[] = [
    // TODO: refactor these columns if possible

    // Status column with colored dot
    createStatusWithDotColumn<AdminOrganizationFormData>('status', 'Status'),

    // Organization name
    {
        accessorKey: 'organization.name',
        header: 'Name',
        cell: (info) => {
            return info.getValue() ?? 'Not found';
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            // Rows are the pre-sorted shape organization-view builds; the prop type is the base form data.
            const a = (rowA.original as SortableAdminOrganizationFormData).sort_name || '';
            const b = (rowB.original as SortableAdminOrganizationFormData).sort_name || '';
            return a.localeCompare(b);
        },
    },

    // Empty spacer column
    {
        id: 'spacer',
        header: '',
        cell: () => '',
        enableSorting: false,
    },

    // Primary Contact (full name from first + last)
    createFullNameColumn<AdminOrganizationFormData>('contact_first_name', 'contact_last_name', 'Primary Contact'),

    // Responsible Admin with colored dot
    {
        id: 'responsible_admin',
        accessorFn: (row) => {
            return row.status === 'WAITING FOR APPROVAL' ? 'NOC' : 'CDIM';
        },
        header: 'Responsible Admin',
        cell: (info) => {
            const value = info.getValue() as string;
            return h(TagComponent, {
                value,
                variant: 'info',
                showDot: true,
                className: 'mx-xs',
            });
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            const a = (rowA.original as SortableAdminOrganizationFormData).sort_admin || '';
            const b = (rowB.original as SortableAdminOrganizationFormData).sort_admin || '';
            return a.localeCompare(b);
        },
    },

    // Next Step tag
    {
        accessorKey: 'next_step',
        header: 'Next Step',
        cell: (info) => {
            const value = info.getValue() as string;

            if (value === 'Review') {
                return '';
            }

            const variantMap: Record<string, TagVariant> = {
                'Role Pool': 'info',
                'Default Groups': 'info',
                'IN PROGRESS': 'info',
                Summary: 'hold',
                Review: 'hold',
            };

            const variant = variantMap[value] || 'default';

            return h(TagComponent, {
                value: value.length > 0 ? value : 'Done',
                variant,
                className: 'text-md capitalize',
            });
        },
        enableSorting: true,
    },
];
</script>
